import { createContext, useCallback, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material";

const CarouselRoot = styled("section")({});

type CarouselRootProps = typeof CarouselRoot extends (props: infer P) => any ? P : never;

export interface CarouselContextProps {
  /** @description Zero based index of the visible slide. */
  activeStep: number;
  /** @description Number of slides rendered by `<CarouselSlides />`. */
  steps: number;
  isLastStep: boolean;
  /** @description Id of the slides container, referenced by the controls through `aria-controls`. */
  slidesId: string;
}

export const CarouselContext = createContext<CarouselContextProps | undefined>(undefined);
CarouselContext.displayName = "CarouselContext";

export interface CarouselActionsContextProps {
  /** @description Shows the following slide. Does nothing on the last one. */
  next: () => void;
  /** @description Shows the preceding slide. Does nothing on the first one. */
  previous: () => void;
  /** @description Kept in sync by `<CarouselSlides />` with the slides it renders. */
  setSteps: (steps: number) => void;
}

export const CarouselActionsContext = createContext<CarouselActionsContextProps | undefined>(undefined);
CarouselActionsContext.displayName = "CarouselActionsContext";

/** An accessible name is required: without one, the `<section>` is not exposed as a region. */
export type CarouselProps = Omit<CarouselRootProps, "aria-label" | "aria-labelledby"> &
  ({ "aria-label": string } | { "aria-labelledby": string });

const INITIAL_STATE = { activeStep: 0, steps: 0 };

/**
 * @description Shows one slide at a time, following the WAI-ARIA carousel pattern without auto-rotation.
 * Compose it with `<CarouselSlides />`, `<CarouselSlide />`, `<CarouselStepper />`, `<CarouselBackButton />`
 * and `<CarouselNextButton />`, or build custom controls with `useCarousel` and `useCarouselActions`.
 *
 * The active slide is tracked by position, so adding or removing slides before it changes which one is shown.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/carousel/
 */
function Carousel(props: CarouselProps) {
  const { children, ...rest } = props;

  const { t } = useTranslation();
  const slidesId = useId();

  const [{ activeStep, steps }, setState] = useState(INITIAL_STATE);

  const next = useCallback(() => {
    setState((state) => (state.activeStep >= state.steps - 1 ? state : { ...state, activeStep: state.activeStep + 1 }));
  }, []);

  const previous = useCallback(() => {
    setState((state) => (state.activeStep <= 0 ? state : { ...state, activeStep: state.activeStep - 1 }));
  }, []);

  const setSteps = useCallback((steps: number) => {
    setState((state) =>
      state.steps === steps ? state : { steps, activeStep: Math.min(state.activeStep, Math.max(steps - 1, 0)) }
    );
  }, []);

  const context = useMemo(
    () => ({ activeStep, steps, isLastStep: activeStep >= steps - 1, slidesId }),
    [activeStep, steps, slidesId]
  );

  const actions = useMemo(() => ({ next, previous, setSteps }), [next, previous, setSteps]);

  return (
    <CarouselActionsContext.Provider value={actions}>
      <CarouselContext.Provider value={context}>
        <CarouselRoot aria-roledescription={t("global.carousel_role_description")} {...rest}>
          {children}
        </CarouselRoot>
      </CarouselContext.Provider>
    </CarouselActionsContext.Provider>
  );
}

export default Carousel;
